/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Services/PoliciesViewService" />
import { GitStatusContext } from "Repos/Common/Contracts/Git";
import { PolicyConfiguration, PolicyTypeRef } from "Repos/Common/Contracts/Policy";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import {
    BuildDefinitionSummary,
    IAutomaticReviewerPolicy,
    IAutomaticReviewerPolicyComplexSettings,
    IBuildPolicy,
    IBuildPolicyComplexSettings,
    IPolicy,
    IProject,
    IStatusPolicy,
    IStatusPolicyComplexSettings,
    InputType,
    PolicyInputInheritanceType,
    Scope,
    SelectableItem
} from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableArray, IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import * as VS_Graph from "VSS/Platform/Contracts/Graph";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IListBoxItem, ListBoxItemType, LoadingCell } from "VSSUI/ListBox";
import { ITableColumn } from "VSSUI/Table";
import * as React from "react";
import { AutomaticReviewerPolicyPanel } from "../Components/AutomaticReviewerPolicyPanel";
import { BuildPoliciesPanel } from "../Components/BuildPoliciesPanel";
import { StatusPoliciesPanel } from "../Components/StatusPoliciesPanel";
import { IGraphRestClient, getGraphClient } from "../RestClient/Graph";
import { getPolicyClient } from "../RestClient/Policy";

export interface IPoliciesViewService extends IVssService {
    deletePolicy(policy: IPolicy, handleError?: (error: any, format?: string) => void): Promise<IPolicy>;
    deleteBuildPolicy(policy: IBuildPolicy): void;
    deleteStatusPolicy(policy: IStatusPolicy): void;
    deleteReviewerPolicy(policy: IAutomaticReviewerPolicy): void;
    getNewPolicy<TPolicy extends IPolicy>(type: string): TPolicy;
    isReady(): IReadonlyObservableValue<boolean>;
    isEditable(): boolean;
    getBuildPolicies(): IReadonlyObservableArray<IBuildPolicy>;
    getStatusPolicies(): IReadonlyObservableArray<IStatusPolicy>;
    getReviewerPolicies(): IReadonlyObservableArray<IAutomaticReviewerPolicy>;
    getTogglePolicies(): IReadonlyObservableArray<IPolicy>;
    getProject(): IProject;
    getSettingsContext(): ISettingsContext;
    goToRef(refName: string, repositoryId: string | undefined): void;
    materializeIdentity(identity: IIdentity): Promise<IIdentity>;
    showEditPanel(policy: IPolicy): void;
    setSettingsContext(settingsContext: ISettingsContext): void;
    savePolicy(policy: IPolicy, handleError?: (error: any, format?: string) => void): Promise<IPolicy | undefined>;
}

interface IPolicyGroup {
    currentScopePolicies?: PolicyConfiguration[];
    enterpriseManagedPolicies?: PolicyConfiguration[];
    inheritedPolicies?: PolicyConfiguration[];
}

interface IRepoPolicyDataProviderData {
    buildDefinitions?: { [key: number]: BuildDefinitionSummary } | undefined;
    identities?: IdentityRef[];
    isEditable?: boolean;
    policyGroups?: { [key: string]: IPolicyGroup };
    recentStatuses?: GitStatusContext[];
    supportServicePrincipals?: boolean;
}

interface IScope {
    repositoryId: string | null;
    refName?: string | null;
    matchKind?: string;
}

interface ISettings {
    complexSettingValueObject?: any;
    settingValue?: string;
    settingValueObject?: any;
}

interface ISettingsContext {
    goToProject: () => void;
    goToRef?: (ref: string, repositoryId?: string) => void;
    repositoryId?: string;
    repositoryName?: string;
    refName?: string;
}

interface BuildPolicySettings {
    buildDefinitionId: number;
    displayName: string | null;
    queueOnSourceUpdateOnly: boolean;
    manualQueueOnly: boolean;
    validDuration: number;
    filenamePatterns?: string[];
    hasQueueBuildPermission: boolean;
}

interface BuildDefQueryResponse {
    definitions?: BuildDefinitionSummary[];
    continuationToken?: string | null;
}

interface StatusPolicySettings {
    authorId: string | null;
    defaultDisplayName: string | null;
    filenamePatterns?: string[];
    invalidateOnSourceUpdate: boolean;
    policyApplicability: 1 | null;
    statusName: string;
    statusGenre: string | null;
}

interface RequiredReviewerPolicySettings {
    creatorVoteCounts: boolean;
    filenamePatterns?: string[];
    message?: string;
    minimumApproverCount: number;
    requiredReviewerIds: string[];
}

export class PoliciesViewService extends VssService implements IPoliciesViewService {
    private buildDefs: IObservableArray<IListBoxItem<BuildDefinitionSummary>> | undefined;
    private buildDefinitionsContinuationToken: string = "";
    private buildDefinitionsLoading = new ObservableValue(false);
    private buildDefsForExistingPolicies: { [key: number]: BuildDefinitionSummary } = {};
    private isEditableValue: boolean = false;
    private isEditPanelVisible = false;
    private isReadyValue = new ObservableValue(false);
    private buildPolicies = new ObservableArray<IBuildPolicy>();
    private statusPolicies = new ObservableArray<IStatusPolicy>();
    private statusChecksSupportServicePrincipals: boolean = false;
    private reviewerPolicies = new ObservableArray<IAutomaticReviewerPolicy>();
    private togglePolicies = new ObservableArray<IPolicy>();
    private identities: { [id: string]: IIdentity } = {};
    private pageHistoryService: IPageStateHistoryService;
    private projectCache: IProject | undefined;
    private recentStatuses = new ObservableValue<GitStatusContext[] | undefined>(undefined);
    private readonly selectedBuildId = new ObservableValue<number | undefined>(undefined);
    private readonly selectedReviewerId = new ObservableValue<number | undefined>(undefined);
    private readonly selectedStatusId = new ObservableValue<number | undefined>(undefined);
    private settingsContext: ISettingsContext;
    private settingsSvc: ISettingsViewService;

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.settingsSvc = this.pageContext.getService<ISettingsViewService>("SettingsViewService");

        this.pageHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        this.pageHistoryService.registerObservable<number | undefined>(
            this.selectedBuildId,
            "build",
            HistoryBehavior.replace,
            item => (item ? item.toFixed() : ""),
            selected => (selected ? parseInt(selected) : undefined)
        );

        this.pageHistoryService.registerObservable<number | undefined>(
            this.selectedReviewerId,
            "reviewer",
            HistoryBehavior.replace,
            item => (item ? item.toFixed() : ""),
            selected => (selected ? parseInt(selected) : undefined)
        );

        this.pageHistoryService.registerObservable<number | undefined>(
            this.selectedStatusId,
            "status",
            HistoryBehavior.replace,
            item => (item ? item.toFixed() : ""),
            selected => (selected ? parseInt(selected) : undefined)
        );
    };

    public deletePolicy = (policy: IPolicy, handleError?: (error: any, format?: string) => void): Promise<IPolicy> => {
        const policyClient = getPolicyClient(this.pageContext);
        return policyClient
            .deletePolicyConfiguration(this.getProject().id!, policy.id)
            .catch(error => {
                handleError ? handleError(error) : this.settingsSvc.showErrorMessage(error);
                return policy;
            })
            .then(data => {
                if (data) {
                    // there was an error, return existing
                    return data;
                } else {
                    // no error so return defaults
                    const defaultPol = this.getNewPolicy(policy.type);
                    this.copyInheritanceProperties(policy, defaultPol);

                    const index = this.togglePolicies.value.findIndex(p => p.type === defaultPol.type);
                    if (index >= 0) {
                        this.togglePolicies.value[index] = defaultPol;
                    }

                    return defaultPol;
                }
            });
    };

    public deleteBuildPolicy(policy: IBuildPolicy): void {
        this.deletePolicyFromObservableArray(policy, this.buildPolicies);
    }

    public deleteStatusPolicy(policy: IStatusPolicy): void {
        this.deletePolicyFromObservableArray(policy, this.statusPolicies);
    }

    public deleteReviewerPolicy(policy: IAutomaticReviewerPolicy): void {
        this.deletePolicyFromObservableArray(policy, this.reviewerPolicies);
    }

    public getNewPolicy<TPolicy extends IPolicy>(type: string): TPolicy {
        const { repositoryId, refName } = this.settingsContext;
        return policyUtils.getAsPolicy(type, undefined, repositoryId, refName);
    }

    public isReady(): IReadonlyObservableValue<boolean> {
        return this.isReadyValue;
    }

    public isEditable(): boolean {
        return this.isEditableValue;
    }

    public getBuildPolicies(): IReadonlyObservableArray<IBuildPolicy> {
        return this.buildPolicies;
    }

    public getStatusPolicies(): IReadonlyObservableArray<IStatusPolicy> {
        return this.statusPolicies;
    }

    public getReviewerPolicies(): IReadonlyObservableArray<IAutomaticReviewerPolicy> {
        return this.reviewerPolicies;
    }

    public getTogglePolicies(): IReadonlyObservableArray<IPolicy> {
        return this.togglePolicies;
    }

    public materializeIdentity = (identity: IIdentity): Promise<IIdentity> => {
        if (!identity.localId) {
            if (identity.entityType.trim().toLowerCase() === "group") {
                const creationContext: VS_Graph.GraphGroupOriginIdCreationContext = {
                    originId: identity.originId
                } as VS_Graph.GraphGroupOriginIdCreationContext;
                const graphClient: IGraphRestClient = getGraphClient(this.pageContext);
                return graphClient.createGroup(creationContext).then((graphGroup: VS_Graph.GraphGroup) => {
                    return graphClient.getStorageKey(graphGroup.descriptor).then((storageKey: VS_Graph.GraphStorageKeyResult) => {
                        const newIdentity = { ...identity };
                        newIdentity.localId = storageKey.value;
                        return newIdentity;
                    });
                });
            } else if (this.statusChecksSupportServicePrincipals && identity.entityType.trim().toLowerCase() === "serviceprincipal") {
                const creationContext: VS_Graph.GraphServicePrincipalOriginIdCreationContext = {
                    originId: identity.originId
                } as VS_Graph.GraphServicePrincipalOriginIdCreationContext;
                const graphClient: IGraphRestClient = getGraphClient(this.pageContext);
                return graphClient.createServicePrincipal(creationContext).then((graphServicePrincipal: VS_Graph.GraphServicePrincipal) => {
                    return graphClient.getStorageKey(graphServicePrincipal.descriptor).then((storageKey: VS_Graph.GraphStorageKeyResult) => {
                        const newIdentity = { ...identity };
                        newIdentity.localId = storageKey.value;
                        return newIdentity;
                    });
                });
            } else {
                const creationContext: VS_Graph.GraphUserOriginIdCreationContext = {
                    originId: identity.originId
                } as VS_Graph.GraphUserOriginIdCreationContext;
                const graphClient: IGraphRestClient = getGraphClient(this.pageContext);
                return graphClient.createUser(creationContext).then((graphUser: VS_Graph.GraphUser) => {
                    return graphClient.getStorageKey(graphUser.descriptor).then((storageKey: VS_Graph.GraphStorageKeyResult) => {
                        const newIdentity = { ...identity };
                        newIdentity.localId = storageKey.value;
                        return newIdentity;
                    });
                });
            }
        }

        // already materizled (have local id) so return current item
        return Promise.resolve({ ...identity });
    };

    public loadPolicies(): void {
        const { repositoryId, refName } = this.settingsContext;
        const key = `${repositoryId}.${refName}`;

        const dataProviderProps = {
            projectId: this.getProject().id,
            repositoryId: repositoryId,
            refName: refName
        };

        // race condition happens easily here with page history clicks, so trying to avoid multiple calls for invalid data with short 15ms timeout
        setTimeout(() => {
            const { repositoryId, refName } = this.settingsContext;
            const currentKey = `${repositoryId}.${refName}`;
            if (currentKey === key) {
                const contributionId = refName
                    ? "ms.vss-code-web.branch-policies-data-provider"
                    : "ms.vss-code-web.repository-policies-data-provider";
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                contributionService
                    .getDataAsync(contributionId, dataProviderProps, true) // need to ignore cache
                    .catch(this.settingsSvc.showErrorMessage)
                    .then((data: IRepoPolicyDataProviderData) => {
                        const { repositoryId, refName } = this.settingsContext;
                        const currentKey = `${repositoryId}.${refName}`;
                        if (data && data.supportServicePrincipals) {
                            this.statusChecksSupportServicePrincipals = data.supportServicePrincipals;
                        }

                        if (currentKey === key) {
                            data = data || {};
                            if (data.recentStatuses) {
                                this.recentStatuses.value = data.recentStatuses;
                            }
                            if (!this.recentStatuses.value) {
                                this.recentStatuses.value = [];
                            }
                            if (data.buildDefinitions) {
                                this.buildDefsForExistingPolicies = data.buildDefinitions;
                            }
                            if (data.identities) {
                                for (const identity of data.identities) {
                                    this.identities[identity.id] = convertToIdentity(identity);
                                }
                            }

                            this.isEditableValue = Boolean(data.isEditable);

                            data.policyGroups = data.policyGroups || {};
                            const policiesGuids = refName ? branchPolicyGuids : globalPolicyGuids;
                            const toggles: IPolicy[] = [];
                            policiesGuids.forEach(type => {
                                if (!featureFlags[type] || isFeatureFlagEnabled(this.pageContext, featureFlags[type], false)) {
                                    const policyGroup = data.policyGroups![type] || {};
                                    if (singletonPolicies[type]) {
                                        const currentScopePolicy =
                                            policyGroup.currentScopePolicies && policyGroup.currentScopePolicies.length
                                                ? policyGroup.currentScopePolicies[0]
                                                : undefined;

                                        const policy: IPolicy = policyUtils.getAsPolicy(type, currentScopePolicy, repositoryId, refName);

                                        if (policyGroup.currentScopePolicies && policyGroup.currentScopePolicies.length > 1) {
                                            // if singleton and more than one policy at current scope, then set additional if they are active (value is true)
                                            policy.additonalCurrentScopePolicies = policyGroup.currentScopePolicies
                                                .slice(1, policyGroup.currentScopePolicies.length)
                                                .map(pol => policyUtils.getAsPolicy(type, pol))
                                                .filter(pol => pol.value);

                                            if (!policy.additonalCurrentScopePolicies.length) {
                                                policy.additonalCurrentScopePolicies = undefined;
                                            }
                                        }

                                        if (policyGroup.enterpriseManagedPolicies) {
                                            policy.enterprisePolicies = policyGroup.enterpriseManagedPolicies.map(pol =>
                                                policyUtils.getAsPolicy(type, pol)
                                            );
                                        }

                                        if (policyGroup.inheritedPolicies) {
                                            policy.inheritedPolicies = policyGroup.inheritedPolicies.map(pol => policyUtils.getAsPolicy(type, pol));
                                        }

                                        policy.disabled = !data.isEditable;
                                        policyUtils.setActiveAndInheritedDetails(type, policy);

                                        toggles.push(policy);
                                    } else if (type === policyTypes.build) {
                                        const policiesToAdd = processPolicyGroup(policyGroup, type, data.isEditable!) as IBuildPolicy[];

                                        for (const policy of policiesToAdd) {
                                            const settings = policy.complexSettings!;
                                            settings.buildDefinition =
                                                this.buildDefsForExistingPolicies[settings.buildDefinitionId] ||
                                                this.buildDefs?.value.find(def => def.data?.id === settings.buildDefinitionId);
                                        }

                                        this.buildPolicies.push(...policiesToAdd);
                                    } else if (type === policyTypes.statusChecks) {
                                        const policiesToAdd = processPolicyGroup(policyGroup, type, data.isEditable!) as IStatusPolicy[];

                                        for (const policy of policiesToAdd) {
                                            const { authorId } = policy.complexSettings!;
                                            if (authorId.length) {
                                                policy.complexSettings!.author = this.identities[authorId] || ({ id: authorId } as IdentityRef);
                                            }
                                        }

                                        this.statusPolicies.push(...policiesToAdd);
                                    } else if (type === policyTypes.requiredReviewers) {
                                        const policiesToAdd = processPolicyGroup(policyGroup, type, data.isEditable!) as IAutomaticReviewerPolicy[];

                                        for (const policy of policiesToAdd) {
                                            policy.complexSettings!.reviewers = policy.complexSettings!.reviewerIds.map(
                                                id => this.identities[id] || ({ id } as IdentityRef)
                                            );
                                        }

                                        this.reviewerPolicies.push(...policiesToAdd);
                                    }
                                }
                            });

                            toggles.sort((a, b) => (a.order || 0) - (b.order || 0));
                            this.togglePolicies.push(...toggles);

                            this.isReadyValue.value = true;

                            // If there was a selected policy, show its panel
                            // Except it's already visible because we displayed it while we navigated to the inherited policy.
                            if (!this.isEditPanelVisible) {
                                if (this.selectedBuildId.value) {
                                    const policy = this.buildPolicies.value.find(p => p.id === this.selectedBuildId.value);
                                    if (policy) {
                                        this.showEditPanel(policy);
                                    }
                                } else if (this.selectedStatusId.value) {
                                    const policy = this.statusPolicies.value.find(p => p.id === this.selectedStatusId.value);
                                    if (policy) {
                                        this.showEditPanel(policy);
                                    }
                                } else if (this.selectedReviewerId.value) {
                                    const policy = this.reviewerPolicies.value.find(p => p.id === this.selectedReviewerId.value);
                                    if (policy) {
                                        this.showEditPanel(policy);
                                    }
                                }
                            }
                        }
                    });
            }
        }, 15);
    }

    public getProject = (): IProject => {
        if (!this.projectCache) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageServiceData = tfsPageService.getData();
            const project = pageServiceData && pageServiceData.project;

            this.projectCache = {
                name: project && project.name,
                id: project && project.id
            };
        }
        return this.projectCache;
    };

    public getSettingsContext(): ISettingsContext {
        return this.settingsContext;
    }

    public goToRef(refName: string, repositoryId: string | undefined): void {
        if (this.settingsContext && this.settingsContext.goToRef) {
            this.settingsContext.goToRef(refName, repositoryId);
        }
    }

    public showEditPanel(policy: IPolicy): void {
        if (policy.type === policyTypes.build) {
            this.selectedBuildId.value = policy.id;
            this.isEditPanelVisible = true;
            this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => (
                <BuildPoliciesPanel
                    buildDefinitions={this.getBuildDefinitions()}
                    buildDefinitionsLoading={this.buildDefinitionsLoading}
                    policy={policy as IBuildPolicy}
                    onDismiss={() => {
                        this.selectedBuildId.value = undefined;
                        onDismiss();
                        this.isEditPanelVisible = false;
                    }}
                    save={(policy, handleError) => {
                        this.savePolicy(policy, handleError).then(savedPolicy => {
                            if (savedPolicy) {
                                this.selectedBuildId.value = undefined;
                                this.isEditPanelVisible = false;
                                onDismiss();
                            }
                        });
                    }}
                />
            ));
        } else if (policy.type === policyTypes.statusChecks) {
            this.selectedStatusId.value = policy.id;
            this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => (
                <StatusPoliciesPanel
                    splitStatus={policyUtils.splitStatus}
                    policy={policy as IStatusPolicy}
                    materializeIdentity={this.materializeIdentity}
                    onDismiss={() => {
                        this.selectedStatusId.value = undefined;
                        onDismiss();
                        this.isEditPanelVisible = false;
                    }}
                    recentStatuses={this.recentStatuses}
                    save={(policy, handleError) => {
                        this.savePolicy(policy, handleError).then(savedPolicy => {
                            if (savedPolicy) {
                                this.selectedStatusId.value = undefined;
                                this.isEditPanelVisible = false;
                                onDismiss();
                            }
                        });
                    }}
                    supportServicePrincipals={this.statusChecksSupportServicePrincipals}
                />
            ));
        } else if (policy.type === policyTypes.requiredReviewers) {
            this.selectedReviewerId.value = policy.id;
            this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout(onDismiss => (
                <AutomaticReviewerPolicyPanel
                    policy={policy as IAutomaticReviewerPolicy}
                    materializeIdentity={this.materializeIdentity}
                    onDismiss={() => {
                        this.selectedReviewerId.value = undefined;
                        onDismiss();
                        this.isEditPanelVisible = false;
                    }}
                    save={(policy, handleError) => {
                        this.savePolicy(policy, handleError).then(savedPolicy => {
                            if (savedPolicy) {
                                this.selectedReviewerId.value = undefined;
                                this.isEditPanelVisible = false;
                                onDismiss();
                            }
                        });
                    }}
                />
            ));
        }
    }

    public setSettingsContext(settingsContext: ISettingsContext): void {
        if (
            this.settingsContext &&
            this.settingsContext.repositoryId === settingsContext.repositoryId &&
            this.settingsContext.refName === settingsContext.refName
        ) {
            return;
        }

        this.settingsContext = settingsContext;

        this.isReadyValue.value = false;
        this.isEditableValue = false;
        this.buildPolicies.value = [];
        this.statusPolicies.value = [];
        this.reviewerPolicies.value = [];
        this.togglePolicies.value = [];
        this.buildDefs = undefined;
        this.buildDefinitionsContinuationToken = "";
        this.buildDefinitionsLoading.value = false;
        this.loadPolicies();
    }

    public savePolicy = (policy: IPolicy, handleError?: (error: any, format?: string) => void): Promise<IPolicy | undefined> => {
        // Remember the reviewers that come from the picker so we don't need to resolve them again
        if (policy.type === policyTypes.requiredReviewers) {
            for (const identity of (policy as IAutomaticReviewerPolicy).complexSettings!.reviewers) {
                this.identities[identity.localId!] = identity;
            }
        }

        if (policy.type === policyTypes.statusChecks) {
            const { author } = (policy as IStatusPolicy).complexSettings!;
            if (author) {
                this.identities[author.localId!] = author;
            }
        }

        const isNew = policy.id === -1;
        let promise: Promise<PolicyConfiguration>;
        if (isNew) {
            const { repositoryId, refName } = this.settingsContext;
            const creationConfig = policyUtils.generateServicePolicyForCreation(policy, repositoryId, refName);
            promise = getPolicyClient(this.pageContext)
                .createPolicyConfiguration(creationConfig, this.getProject().id!)
                .then(data => data || { ...policy, value: false });
        } else {
            if (!policy.value && singletonPolicies[policy.type]) {
                // clear settings by assigning default value
                policyUtils.getSetting(policy.type, policy.config, policyUtils.generatePolicy(policy.type).settingValue);
            }
            policyUtils.copyToConfig(policy.type, policy, policy.config);

            promise = getPolicyClient(this.pageContext).updatePolicyConfiguration(policy.config, this.getProject().id!, policy.id);
        }

        return promise.catch(handleError || this.settingsSvc.showErrorMessage).then(data => {
            const updatedPolicy = data ? policyUtils.getAsPolicy(policy.type, data) : undefined;
            let isTogglePolicy = false;
            if (updatedPolicy) {
                let observableArray: IObservableArray<IPolicy> | undefined = undefined;
                if (policy.type === policyTypes.build) {
                    observableArray = this.buildPolicies;

                    // Use known build definition
                    const updatedBuildPolicy = updatedPolicy as IBuildPolicy;
                    const settings = updatedBuildPolicy.complexSettings!;
                    settings.buildDefinition =
                        this.buildDefsForExistingPolicies[settings.buildDefinitionId] ||
                        this.buildDefs?.value.find(def => def.data?.id === settings.buildDefinitionId)?.data;
                } else if (policy.type === policyTypes.statusChecks) {
                    observableArray = this.statusPolicies;
                    const updatedStatusPolicy = updatedPolicy as IStatusPolicy;
                    const { authorId } = updatedStatusPolicy.complexSettings!;
                    if (authorId.length) {
                        updatedStatusPolicy.complexSettings!.author = this.identities[authorId] || ({ id: authorId } as IdentityRef);
                    }
                } else if (policy.type === policyTypes.requiredReviewers) {
                    observableArray = this.reviewerPolicies;

                    // Fill in known identities
                    const updatedReviewerPolicy = updatedPolicy as IAutomaticReviewerPolicy;
                    updatedReviewerPolicy.complexSettings!.reviewers = updatedReviewerPolicy.complexSettings!.reviewerIds.map(
                        id => this.identities[id] || ({ id } as IdentityRef)
                    );
                } else {
                    // only needed for toggle policies
                    isTogglePolicy = true;
                    this.copyInheritanceProperties(policy, updatedPolicy);
                }

                if (observableArray) {
                    const index = observableArray.value.findIndex(p => p.id === policy.id);
                    if (index >= 0) {
                        observableArray.splice(index, 1, updatedPolicy);
                    } else {
                        observableArray.push(updatedPolicy);
                    }
                }
            }
            if (isTogglePolicy) {
                const index = this.togglePolicies.value.findIndex(p => p.type === updatedPolicy?.type);
                if (index >= 0) {
                    this.togglePolicies.value[index] = updatedPolicy as IPolicy;
                }
            }
            return updatedPolicy;
        });
    };

    private getBuildDefinitions = (): IReadonlyObservableArray<IListBoxItem<BuildDefinitionSummary>> => {
        if (!this.buildDefs) {
            const knownDefinitionItems = Object.keys(this.buildDefsForExistingPolicies).map(key =>
                convertDefinitionToListBoxItem(this.buildDefsForExistingPolicies[key as any])
            );

            this.buildDefs = new ObservableArray([
                ...knownDefinitionItems,
                {
                    id: "loading-row",
                    type: ListBoxItemType.Loading,
                    render: (_rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) => {
                        this.loadMoreBuildDefinitions();
                        return <LoadingCell key={tableItem.id} columnIndex={columnIndex} tableColumn={tableColumn} tableItem={tableItem} />;
                    }
                }
            ]);

            this.loadMoreBuildDefinitions();
        }
        return this.buildDefs;
    };

    private loadMoreBuildDefinitions = () => {
        if (!this.buildDefinitionsLoading.value) {
            const { repositoryId, refName } = this.settingsContext;
            this.buildDefinitionsLoading.value = true;

            const dataProviderId = "ms.vss-code-web.admin-policies-build-definitions-data-provider";
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const dataProviderProps = {
                projectId: this.getProject().id,
                continuationToken: this.buildDefinitionsContinuationToken
            };

            return contributionService
                .getDataAsync(dataProviderId, dataProviderProps)
                .catch(this.settingsSvc.showErrorMessage)
                .then((data: BuildDefQueryResponse) => {
                    if (repositoryId !== this.settingsContext.repositoryId || refName !== this.settingsContext.refName) {
                        return; //Context has changed ignore the result of returned request
                    }

                    // Drop loading-row it if there's no more data to fetch
                    if (!data?.continuationToken) {
                        this.buildDefs?.removeAll(i => i.id === "loading-row");
                    }

                    this.buildDefinitionsContinuationToken = data?.continuationToken || "";
                    this.buildDefinitionsLoading.value = false;

                    if (data?.definitions) {
                        const newDefinitions = data.definitions.filter(d => !this.buildDefsForExistingPolicies[d.id]);

                        // Insert new definitions before the loading-row,
                        this.buildDefs?.splice(
                            data.continuationToken ? this.buildDefs.length - 1 : this.buildDefs.length,
                            0,
                            ...newDefinitions.map(convertDefinitionToListBoxItem)
                        );
                    }
                });
        }
    };

    private copyInheritanceProperties(from: IPolicy, to: IPolicy) {
        to.inheritedPolicies = from.inheritedPolicies;
        to.additonalCurrentScopePolicies = from.additonalCurrentScopePolicies;
        to.enterprisePolicies = from.enterprisePolicies;
        policyUtils.setActiveAndInheritedDetails(to.type, to);
    }

    private deletePolicyFromObservableArray(policy: IPolicy, array: IObservableArray<IPolicy>): void {
        getPolicyClient(this.pageContext)
            .deletePolicyConfiguration(this.getProject().id!, policy.id)
            .then(() => {
                array.removeAll(p => p.id === policy.id);
            })
            .catch(error => this.settingsSvc.showErrorMessage(error));
    }
}

export function getPoliciesViewService(pageContext: IVssPageContext): IPoliciesViewService {
    return pageContext.getService<IPoliciesViewService>("PoliciesViewService");
}

Services.add("PoliciesViewService", { serviceFactory: PoliciesViewService });

const megabyte = 1048576;
const policySettingKeys = {
    authorEmail: "authorEmailPatterns",
    pathMatch: "filenamePatterns",
    pathLength: "maxPathLength",
    fileSize: "maximumGitBlobSizeInBytes",
    caseSensitivity: "enforceConsistentCase",
    limitMergeTypes: {
        allowNoFastForward: "allowNoFastForward",
        allowSquash: "allowSquash",
        allowRebase: "allowRebase",
        allowRebaseMerge: "allowRebaseMerge"
    },
    minimumReviewersStr: "minimumApproverCount",
    minimumReviewersObj: {
        allowDownvotes: "allowDownvotes",
        blockLastPusherVote: "blockLastPusherVote",
        creatorVoteCounts: "creatorVoteCounts",
        requireVoteOnLastIteration: "requireVoteOnLastIteration",
        resetOnSourcePush: "resetOnSourcePush",
        resetRejectionsOnSourcePush: "resetRejectionsOnSourcePush",
        requireVoteOnEachIteration:"requireVoteOnEachIteration"
    },
    minimumReviewersOnSourceChangeKey: "minimumReviewersOnSourceChangeKey",
    scope: "scope"
};

export const policyTypes = {
    authorEmail: "77ed4bd3-b063-4689-934a-175e4d0a78d7",
    pathMatch: "51c78909-e838-41a2-9496-c647091e3c61",
    caseSensitivty: "7ed39669-655c-494e-b4a0-a08b4da0fcce",
    fileName: "db2b9b4c-180d-4529-9701-01541d19f36b",
    pathLength: "001a79cf-fda1-4c4e-9e7c-bac40ee5ead8",
    fileSize: "2e26e725-8201-4edd-8bf5-978563c34a80",
    credential: "e67ae10f-cf9a-40bc-8e66-6b3a8216956e",
    minimumReviewers: "fa4e907d-c16b-4a4c-9dfa-4906e5d171dd",
    linkedWorkItems: "40e92b44-2fe1-4dd6-b3d8-74a9c21d0c6e",
    commentResolution: "c6a1889d-b943-4856-b76f-9e46bb6b0df2",
    limitMergeTypes: "fa4e907d-c16b-4a4c-9dfa-4916e5d171ab",
    build: "0609b952-1397-4640-95ec-e00a01b2c241",
    statusChecks: "cbdc66da-9728-4af8-aada-9a5a32e4a226",
    requiredReviewers: "fd2167ab-b0be-447a-8ec8-39368250530e"
};

const branchPolicyGuids = [
    policyTypes.minimumReviewers,
    policyTypes.linkedWorkItems,
    policyTypes.commentResolution,
    policyTypes.limitMergeTypes,
    policyTypes.build,
    policyTypes.statusChecks,
    policyTypes.requiredReviewers
];

const globalPolicyGuids = [
    policyTypes.authorEmail,
    policyTypes.pathMatch,
    policyTypes.caseSensitivty,
    policyTypes.fileName,
    policyTypes.pathLength,
    policyTypes.fileSize,
    policyTypes.credential
];

const featureFlags = {
    [policyTypes.authorEmail]: "Git.Push.CommitAuthorPushPolicy",
    [policyTypes.pathMatch]: "Git.Push.NewPushPolicies",
    [policyTypes.credential]: "WebAccess.VersionControl.GitSecretsScanning",
};

const requiredOrOptional = {
    optional: Resources.Optional,
    required: Resources.Required
};

const matchKind = {
    default: "DefaultBranch",
    exact: "Exact",
    prefix: "Prefix"
};

const policySettingDefaults = {
    pathLength: "248",
    fileSize: "10",
    required: requiredOrOptional.required,
    minimumReviewersStr: "2",
    minimumReviewersObj: {
        allowDownvotes: false,
        creatorVoteCounts: false,
        resetOnSourcePush: false,
        requireVoteOnLastIteration: false,
        resetRejectionsOnSourcePush: false,
        blockLastPusherVote: false,
        requireVoteOnEachIteration: false
    },
    limitMergeTypes: {
        allowNoFastForward: true,
        allowSquash: true,
        allowRebase: true,
        allowRebaseMerge: true
    },
    build: {
        buildDefinitionId: -1,
        displayName: "",
        filenamePatterns: "",
        manualQueueOnly: false,
        required: true,
        queueOnSourceUpdateOnly: true,
        validDuration: "12"
    } as IBuildPolicyComplexSettings,
    reviewer: {
        creatorVoteCounts: true,
        filenamePatterns: "",
        message: "",
        minimumApproverCount: 1,
        required: true,
        reviewerIds: [],
        reviewers: []
    } as IAutomaticReviewerPolicyComplexSettings,
    status: {
        authorId: "",
        defaultDisplayName: "",
        filenamePatterns: "",
        invalidateOnSourceUpdate: false,
        policyApplicability: false,
        required: true,
        status: ""
    } as IStatusPolicyComplexSettings,
    other: ""
};

const singletonPolicies = {
    [policyTypes.authorEmail]: true,
    [policyTypes.pathMatch]: true,
    [policyTypes.caseSensitivty]: true,
    [policyTypes.fileName]: true,
    [policyTypes.pathLength]: true,
    [policyTypes.fileSize]: true,
    [policyTypes.credential]: true,
    [policyTypes.minimumReviewers]: true,
    [policyTypes.linkedWorkItems]: true,
    [policyTypes.commentResolution]: true,
    [policyTypes.limitMergeTypes]: true
};

const serviceCreatePolicy = {
    type: "",
    revision: 1,
    isDeleted: false,
    isBlocking: true,
    isEnabled: true,
    settings: {}
} as unknown as PolicyConfiguration;

const validation = {
    atLeastOneMergeType: function (data: string, policy?: IPolicy, settingValueObject?: { [key: string]: boolean }): string | undefined {
        return settingValueObject && Object.keys(settingValueObject).length > 0 ? undefined : Resources.ValidationMergeType;
    },
    isNumber: function (data: string): string | undefined {
        return data.length > 0 && data.indexOf(".") < 0 && !isNaN(Number(data)) ? undefined : Resources.ValidationNumber;
    },
    inheritanceFileSizeIsLess: function (data: string, policy: IPolicy): string | undefined {
        let inheritedSettingValue = policy.inheritedSettingValue;
        if (inheritedSettingValue === undefined) {
            const policies = policy.activeInheritedAndEnterprisePolicies;
            if (policies && policies.length) {
                const values = policies
                    .filter(pol => !!pol.settingValue && pol.settingValue.length > 0 && !isNaN(Number(pol.settingValue)))
                    .map(pol => Number(pol.settingValue));
                if (values && values.length) {
                    inheritedSettingValue = Math.min(...values).toString();
                }
            }
        }

        return inheritedSettingValue === undefined || Number(data) <= Number(inheritedSettingValue)
            ? undefined
            : format(Resources.PolicyInheritanceFileSize, inheritedSettingValue);
    },
    minimumReviewersBetween1And10: function (data: string): string | undefined {
        return Number(data) >= 1 && Number(data) <= 10 ? undefined : Resources.ValidationMinReviewersCount;
    },
    pathLengthIsBetween1And10000: function (data: string): string | undefined {
        return Number(data) >= 1 && Number(data) <= 10000 ? undefined : Resources.ValidationPathLength;
    },
    inheritancePathLengthIsLess: function (data: string, policy: IPolicy): string | undefined {
        let inheritedSettingValue = policy.inheritedSettingValue;
        if (inheritedSettingValue === undefined) {
            const policies = policy.activeInheritedAndEnterprisePolicies;
            if (policies && policies.length) {
                const values = policies
                    .filter(pol => !!pol.settingValue && pol.settingValue.length > 0 && !isNaN(Number(pol.settingValue)))
                    .map(pol => Number(pol.settingValue));
                if (values && values.length) {
                    inheritedSettingValue = Math.min(...values).toString();
                }
            }
        }

        return inheritedSettingValue === undefined || Number(data) <= Number(inheritedSettingValue)
            ? undefined
            : format(Resources.PolicyInheritancePathLength, inheritedSettingValue);
    }
};

const policyGeneratorMap: { [key: string]: IPolicy } = {
    [policyTypes.authorEmail]: {
        description: Resources.PolicyAuthorEmailDesc,
        id: -1,
        input: {
            showInheritedValuesTable: true,
            type: InputType.Text
        },
        order: 100,
        settingValue: policySettingDefaults.other,
        title: Resources.PolicyAuthorEmailTitle,
        tooltip: Resources.PolicyAuthorEmailTooltip,
        type: policyTypes.authorEmail,
        value: false
    },
    [policyTypes.pathMatch]: {
        description: Resources.PolicyFilePathValidationDesc,
        id: -1,
        input: {
            showInheritedValuesTable: true,
            type: InputType.Text
        },
        order: 200,
        settingValue: policySettingDefaults.other,
        title: Resources.PolicyFilePathValidationTitle,
        tooltip: Resources.PolicyFilePathValidationTooltip,
        type: policyTypes.pathMatch,
        value: false
    },
    [policyTypes.caseSensitivty]: {
        description: Resources.PolicyCaseEnforcementDesc,
        id: -1,
        link: Resources.PolicyCaseEnforcementLink,
        order: 300,
        title: Resources.PolicyCaseEnforcementTitle,
        type: policyTypes.caseSensitivty,
        value: false
    },
    [policyTypes.fileName]: {
        description: Resources.PolicyReservedNamesDesc,
        id: -1,
        link: Resources.PolicyReservedNamesLink,
        order: 400,
        title: Resources.PolicyReservedNamesTitle,
        type: policyTypes.fileName,
        value: false
    },
    [policyTypes.pathLength]: {
        description: Resources.PolicyMaxPathDesc,
        id: -1,
        input: {
            type: InputType.Number,
            validations: [validation.isNumber, validation.pathLengthIsBetween1And10000, validation.inheritancePathLengthIsLess]
        },
        validationMessge: Resources.PolicyInheritancePathLength,
        link: Resources.PolicyMaxPathLink,
        order: 500,
        settingValue: policySettingDefaults.pathLength,
        title: Resources.PolicyMaxPathTitle,
        type: policyTypes.pathLength,
        value: false
    },
    [policyTypes.fileSize]: {
        description: Resources.PolicyMaxFileSizeDesc,
        id: -1,
        input: {
            type: InputType.List,
            validations: [validation.isNumber, validation.inheritanceFileSizeIsLess],
            options: [
                { id: "1", text: format(Resources.MegaByteAcronymFormat, "1") },
                { id: "2", text: format(Resources.MegaByteAcronymFormat, "2") },
                { id: "5", text: format(Resources.MegaByteAcronymFormat, "5") },
                { id: "10", text: format(Resources.MegaByteAcronymFormat, "10") },
                { id: "50", text: format(Resources.MegaByteAcronymFormat, "50") },
                { id: "100", text: format(Resources.MegaByteAcronymFormat, "100") },
                { id: "200", text: format(Resources.MegaByteAcronymFormat, "200") }
            ]
        },
        validationMessge: Resources.PolicyInheritanceFileSize,
        order: 600,
        settingValue: policySettingDefaults.fileSize,
        title: Resources.PolicyMaxFileSizeTitle,
        type: policyTypes.fileSize,
        value: false
    },
    [policyTypes.credential]: {
        description: Resources.PolicyCredentialCheckDesc,
        id: -1,
        link: Resources.PolicyCredentialCheckLink,
        order: 700,
        title: Resources.PolicyCredentialCheckTitle,
        type: policyTypes.credential,
        value: false
    },
    [policyTypes.minimumReviewers]: {
        description: Resources.PolicyBranchRequiredNumberReviewersDesc,
        id: -1,
        input: {
            type: InputType.MultiSelectWithRadioButtonsAndText,
            inputInheritanceType: PolicyInputInheritanceType.EnabledWithInheritance,
            fullWidth: true,
            useFalseOnDeselect: true,
            singleColumnRender: true,
            title: Resources.MinReviewersCount,
            validations: [validation.isNumber, validation.minimumReviewersBetween1And10],
            options: [
                {
                    id: policySettingKeys.minimumReviewersObj.creatorVoteCounts,
                    value: Resources.MinReviewersAllowOwnChangeApproval
                },
                {
                    id: policySettingKeys.minimumReviewersObj.blockLastPusherVote,
                    value: Resources.MinReviewersProhibitMostRecentPusher
                },
                {
                    id: policySettingKeys.minimumReviewersObj.allowDownvotes,
                    value: Resources.MinReviewersAllowCompleteWithoutVote
                },
                {
                    id: policySettingKeys.minimumReviewersOnSourceChangeKey,
                    value: Resources.MinReviewersVoteChangeOnSourceChangeTitle,
                    subOptions: [
                        {
                            id: policySettingKeys.minimumReviewersObj.requireVoteOnEachIteration,
                            value: Resources.AllIterationsApproved,
                            includes: policySettingKeys.minimumReviewersObj.requireVoteOnLastIteration,
                            link: Resources.AllIterationsApprovedLink,
                            linkDescription: Resources.AllIterationsApprovedLinkDescription
                        },
                        {
                            id: policySettingKeys.minimumReviewersObj.requireVoteOnLastIteration,
                            value: Resources.MinReviewersRequireOneVoteOnLastIteration
                        },
                        {
                            id: policySettingKeys.minimumReviewersObj.resetOnSourcePush,
                            value: Resources.MinReviewersResetApprovalsOnSourcePush
                        },
                        {
                            id: policySettingKeys.minimumReviewersObj.resetRejectionsOnSourcePush,
                            value: Resources.MinReviewersResetAllOnSourcePush,
                            includes: policySettingKeys.minimumReviewersObj.resetOnSourcePush
                        }
                    ]
                }
            ]
        },
        order: 800,
        settingValue: policySettingDefaults.minimumReviewersStr,
        settingValueObject: policySettingDefaults.minimumReviewersObj,
        title: Resources.PolicyBranchRequiredNumberReviewersTitle,
        type: policyTypes.minimumReviewers,
        value: false
    },
    [policyTypes.linkedWorkItems]: {
        description: Resources.PolicyBranchCheckLinkedWorkItemsDesc,
        id: -1,
        input: {
            type: InputType.RadioList,
            options: [
                {
                    id: "required-wit-linked",
                    value: requiredOrOptional.required,
                    description: Resources.PolicyBranchCheckLinkedWorkItemsInputRequired
                },
                {
                    id: "optional-wit-linked",
                    value: requiredOrOptional.optional,
                    description: Resources.PolicyBranchCheckLinkedWorkItemsInputOptional
                }
            ]
        },
        order: 900,
        settingValue: policySettingDefaults.required,
        title: Resources.PolicyBranchCheckLinkedWorkItemsTitle,
        type: policyTypes.linkedWorkItems,
        value: false
    },
    [policyTypes.commentResolution]: {
        description: Resources.PolicyBranchCommentResolutionDesc,
        id: -1,
        input: {
            type: InputType.RadioList,
            options: [
                {
                    id: "required-comment-resolution",
                    value: requiredOrOptional.required,
                    description: Resources.PolicyBranchCommentResolutionInputRequired
                },
                {
                    id: "optional-comment-resolution",
                    value: requiredOrOptional.optional,
                    description: Resources.PolicyBranchCommentResolutionInputOptional
                }
            ]
        },
        order: 1000,
        settingValue: policySettingDefaults.required,
        title: Resources.PolicyBranchCommentResolutionTitle,
        type: policyTypes.commentResolution,
        value: false
    },
    [policyTypes.limitMergeTypes]: {
        description: Resources.PolicyBranchLimitMergeTypesDesc,
        id: -1,
        input: {
            type: InputType.MultiSelect,
            inputInheritanceType: PolicyInputInheritanceType.ReverseInheritance,
            title: Resources.MergeTypesTitle,
            validations: [validation.atLeastOneMergeType],
            options: [
                {
                    id: policySettingKeys.limitMergeTypes.allowNoFastForward,
                    value: Resources.MergeTypesNonFastForward,
                    description: Resources.MergeTypesNonFastForwardDesc
                },
                {
                    id: policySettingKeys.limitMergeTypes.allowSquash,
                    value: Resources.MergeTypesSquash,
                    description: Resources.MergeTypesSquashDesc
                },
                {
                    id: policySettingKeys.limitMergeTypes.allowRebase,
                    value: Resources.MergeTypesFastForwardOnly,
                    description: Resources.MergeTypesFastForwardOnlyDesc
                },
                {
                    id: policySettingKeys.limitMergeTypes.allowRebaseMerge,
                    value: Resources.MergeTypesRebase,
                    description: Resources.MergeTypesRebaseDesc
                }
            ]
        },
        order: 1100,
        settingValueObject: { ...policySettingDefaults.limitMergeTypes },
        title: Resources.PolicyBranchLimitMergeTypesTitle,
        type: policyTypes.limitMergeTypes,
        value: false
    },
    [policyTypes.build]: {
        complexSettings: { ...policySettingDefaults.build },
        id: -1,
        type: policyTypes.build,
        value: true
    },
    [policyTypes.statusChecks]: {
        complexSettings: { ...policySettingDefaults.status },
        id: -1,
        type: policyTypes.statusChecks,
        value: true
    },
    [policyTypes.requiredReviewers]: {
        complexSettings: { ...policySettingDefaults.reviewer },
        id: -1,
        type: policyTypes.requiredReviewers,
        value: true
    }
};

export const policyUtils = {
    convertBToMb: (value: number): string => {
        return (value / megabyte).toString();
    },
    convertMbToB: (value: string): number => {
        const asNum = Number(value);

        return asNum * megabyte;
    },
    copyToConfig: (type: string, viewItem: IPolicy, svcItem: PolicyConfiguration) => {
        policyUtils.getSetting(type, svcItem, viewItem.settingValue, viewItem.settingValueObject, viewItem.complexSettings);
        policyUtils.getValue(type, svcItem, viewItem.value || !!viewItem.inheritedValue);
    },
    formatStringAsArr: (str: string): string[] | null => {
        const filtered = str
            .split(";")
            .map(x => x.trim())
            .filter(x => !!x);
        return filtered.length ? filtered : null;
    },
    generatePolicy: (type: string): IPolicy => {
        return { ...policyGeneratorMap[type] };
    },
    generateServicePolicyForCreation: (policy: IPolicy, repositoryId?: string, refName?: string): PolicyConfiguration => {
        const svcPolicy = { ...serviceCreatePolicy };
        svcPolicy.type = { id: policy.type } as PolicyTypeRef;
        policyUtils.copyToConfig(policy.type, policy, svcPolicy);

        svcPolicy.settings[policySettingKeys.scope] = [policyUtils.getScopeObject(repositoryId, refName)];

        return svcPolicy;
    },
    getAsPolicy<TPolicy extends IPolicy>(type: string, svcItem?: PolicyConfiguration, repositoryId?: string, refName?: string): TPolicy {
        const policy = policyUtils.generatePolicy(type);
        let scope;
        if (svcItem) {
            policy.id = svcItem.id;
            policy.value = policyUtils.getValue(type, svcItem);
            const settings = policyUtils.getSetting(type, svcItem);
            if (settings) {
                policy.settingValue = settings.settingValue;
                policy.settingValueObject = settings.settingValueObject;
                policy.complexSettings = settings.complexSettingValueObject;
            }
            policy.config = svcItem;
            policy.isEnterpriseManaged = svcItem.isEnterpriseManaged;
            if (svcItem.settings && svcItem.settings.scope && svcItem.settings.scope.length) {
                scope = svcItem.settings.scope[0];
            } else {
                scope = policyUtils.getScopeObject(repositoryId, refName);
            }
        } else {
            // set scope from repositoryId and refName
            scope = policyUtils.getScopeObject(repositoryId, refName);
        }

        policyUtils.setScopeProps(scope, policy);

        return policy as TPolicy;
    },
    getScopeObject: (repositoryId?: string, refName?: string): IScope => {
        const scope: IScope = { repositoryId: repositoryId || null };

        if (refName) {
            const wildcardIndex = refName.indexOf("/*");
            if (wildcardIndex >= 0) {
                // wildcard match for ref
                scope.refName = refName.substring(0, wildcardIndex);
                scope.matchKind = matchKind.prefix;
            } else if (wildcardIndex === -1 && refName !== "~default") {
                // actual branch or cross repo match on branch name
                scope.refName = refName;
                scope.matchKind = matchKind.exact;
            } else if (wildcardIndex === -1 && refName === "~default") {
                // default branches
                scope.refName = null;
                scope.matchKind = matchKind.default;
            }
        }

        return scope;
    },
    setScopeProps: (scope: IScope, policy: IPolicy) => {
        policy.repositoryId = scope.repositoryId ? scope.repositoryId : undefined; // want undefined when it could be null
        if (scope.repositoryId && (!scope.refName || scope.matchKind !== matchKind.exact)) {
            policy.scope = Scope.Repository;
            policy.scopeString = Resources.RepositoryScope;
            policy.appliesTo = Resources.EntireRepository;

            if (scope.matchKind === matchKind.prefix && scope.refName) {
                policy.appliesTo = format(Resources.AppliesToCrossRepoBranchPrefix, refToBranchName(scope.refName + "/*"));
                policy.refName = scope.refName + "/*";
            }
        } else if (scope.repositoryId && scope.refName) {
            policy.scope = Scope.Branch;
            policy.scopeString = Resources.Branch;
            policy.appliesTo = Resources.EntireBranch;
            policy.confgiuredOn = refToBranchName(scope.refName);
            policy.refName = scope.refName;
        } else if (scope.matchKind === matchKind.default) {
            policy.scope = Scope.CrossRepoDefaultBranch;
            policy.scopeString = Resources.ProjectScope;
            policy.appliesTo = Resources.AppliesToCrossRepoDefaultBranch;
            policy.refName = "~default";
        } else if (scope.matchKind === matchKind.prefix) {
            policy.scope = Scope.CrossRepoBranchPrefix;
            policy.scopeString = Resources.ProjectScope;
            policy.appliesTo = format(Resources.AppliesToCrossRepoBranchPrefix, refToBranchName(scope.refName + "/*"));
            policy.refName = scope.refName + "/*";
        } else if (scope.matchKind === matchKind.exact && scope.refName) {
            policy.scope = Scope.CrossRepoBranchExact;
            policy.scopeString = Resources.ProjectScope;
            policy.appliesTo = format(Resources.AppliesToCrossRepoBranchExact, scope.refName);
            policy.refName = scope.refName;
        } else {
            policy.scope = Scope.Project;
            policy.scopeString = Resources.ProjectScope;
            policy.appliesTo = Resources.AllRepositories;
        }
    },
    getSetting: (
        type: string,
        policy?: PolicyConfiguration,
        setting?: string,
        settingObject?: { [key: string]: boolean },
        complexSettingObject?: { [key: string]: any }
    ): ISettings | undefined => {
        if (type === policyTypes.authorEmail) {
            if (policy) {
                if (setting !== undefined) {
                    policy.settings[policySettingKeys.authorEmail] = policyUtils.formatStringAsArr(setting);
                }

                return { settingValue: policyUtils.getStringFromArr(policy.settings[policySettingKeys.authorEmail], policySettingDefaults.other) };
            }

            return { settingValue: policySettingDefaults.other };
        }

        if (type === policyTypes.pathMatch) {
            if (policy) {
                if (setting !== undefined) {
                    policy.settings[policySettingKeys.pathMatch] = policyUtils.formatStringAsArr(setting);
                }

                return { settingValue: policyUtils.getStringFromArr(policy.settings[policySettingKeys.pathMatch], policySettingDefaults.other) };
            }

            return { settingValue: policySettingDefaults.other };
        }

        if (type === policyTypes.pathLength) {
            if (policy) {
                if (setting !== undefined) {
                    policy.settings[policySettingKeys.pathLength] = Number(setting);
                }

                return { settingValue: policy.settings[policySettingKeys.pathLength].toString() || policySettingDefaults.pathLength };
            }

            return { settingValue: policySettingDefaults.pathLength };
        }

        if (type === policyTypes.fileSize) {
            if (policy) {
                if (setting !== undefined) {
                    policy.settings[policySettingKeys.fileSize] = policyUtils.convertMbToB(setting);
                }

                return {
                    settingValue: policy.settings[policySettingKeys.fileSize]
                        ? policyUtils.convertBToMb(policy.settings[policySettingKeys.fileSize])
                        : policySettingDefaults.fileSize
                };
            }

            return { settingValue: policySettingDefaults.fileSize };
        }

        if (type === policyTypes.linkedWorkItems || type === policyTypes.commentResolution) {
            if (policy) {
                if (setting !== undefined) {
                    policy.isBlocking = setting === requiredOrOptional.required ? true : false;
                }

                return { settingValue: policy.isBlocking ? requiredOrOptional.required : requiredOrOptional.optional };
            }

            return { settingValue: policySettingDefaults.required };
        }

        if (type === policyTypes.limitMergeTypes) {
            if (policy) {
                if (settingObject !== undefined) {
                    Object.keys(policySettingKeys.limitMergeTypes).forEach(key => {
                        if (settingObject[key]) {
                            policy.settings[key] = true;
                        } else {
                            delete policy.settings[key];
                        }
                    });
                }

                const settingsObj = { ...policy.settings };
                delete settingsObj[policySettingKeys.scope];

                return { settingValueObject: settingsObj };
            }

            return { settingValueObject: policySettingDefaults.limitMergeTypes };
        }

        if (type === policyTypes.minimumReviewers) {
            if (policy) {
                if (settingObject !== undefined) {
                    Object.keys(policySettingKeys.minimumReviewersObj).forEach(key => {
                        policy.settings[key] = settingObject[key];
                    });
                }

                if (setting !== undefined) {
                    policy.settings[policySettingKeys.minimumReviewersStr] = Number(setting);
                }

                const settingsObj = { ...policy.settings };
                delete settingsObj[policySettingKeys.scope];
                delete settingsObj[policySettingKeys.minimumReviewersStr];

                return {
                    settingValue: policy.settings[policySettingKeys.minimumReviewersStr].toString() || policySettingDefaults.minimumReviewersStr,
                    settingValueObject: settingsObj
                };
            }

            return { settingValue: policySettingDefaults.minimumReviewersStr, settingValueObject: { ...policySettingDefaults.limitMergeTypes } };
        }
        if (type === policyTypes.build) {
            if (policy) {
                const policySettings = policy.settings as BuildPolicySettings;
                if (complexSettingObject !== undefined) {
                    const buildSettings = complexSettingObject as IBuildPolicyComplexSettings;

                    policy.isBlocking = buildSettings.required;

                    let filenamePatternsArr: string[] | undefined;
                    if (buildSettings.filenamePatterns && buildSettings.filenamePatterns.length) {
                        filenamePatternsArr = buildSettings.filenamePatterns
                            .split(";")
                            .map(str => str.trim())
                            .filter(str => str.length > 0);
                    }

                    if (filenamePatternsArr && filenamePatternsArr.length) {
                        policySettings.filenamePatterns = filenamePatternsArr;
                    } else {
                        delete policy.settings["filenamePatterns"];
                    }

                    policySettings.buildDefinitionId = Number(buildSettings.buildDefinitionId);
                    policySettings.displayName = buildSettings.displayName.length ? buildSettings.displayName : null;
                    policySettings.manualQueueOnly = buildSettings.manualQueueOnly;
                    policySettings.queueOnSourceUpdateOnly = buildSettings.queueOnSourceUpdateOnly;
                    policySettings.hasQueueBuildPermission = buildSettings.hasQueueBuildPermission;

                    let validDuration = 0;

                    if (buildSettings.queueOnSourceUpdateOnly) {
                        validDuration = Number(buildSettings.validDuration) * 60;
                    }

                    policySettings.validDuration = validDuration;
                }

                const settings: IBuildPolicyComplexSettings = {
                    buildDefinitionId: policySettings.buildDefinitionId,
                    displayName: policySettings.displayName ? policySettings.displayName : "",
                    filenamePatterns:
                        policySettings.filenamePatterns && policySettings.filenamePatterns.length ? policySettings.filenamePatterns.join("; ") : "",
                    manualQueueOnly: policySettings.manualQueueOnly,
                    queueOnSourceUpdateOnly: policySettings.queueOnSourceUpdateOnly,
                    validDuration: (policySettings.validDuration / 60).toString(),
                    required: policy.isBlocking,
                    hasQueueBuildPermission: policySettings.hasQueueBuildPermission
                };

                return {
                    complexSettingValueObject: settings
                };
            }

            return {
                complexSettingValueObject: { ...policySettingDefaults.build }
            };
        }
        if (type === policyTypes.statusChecks) {
            if (policy) {
                const policySettings = policy.settings as StatusPolicySettings;
                if (complexSettingObject !== undefined) {
                    const statusSettings = complexSettingObject as IStatusPolicyComplexSettings;

                    policy.isBlocking = statusSettings.required;

                    let filenamePatternsArr: string[] | undefined;
                    if (statusSettings.filenamePatterns && statusSettings.filenamePatterns.length) {
                        filenamePatternsArr = statusSettings.filenamePatterns
                            .split(";")
                            .map(str => str.trim())
                            .filter(str => str.length > 0);
                    }

                    if (filenamePatternsArr && filenamePatternsArr.length) {
                        policySettings.filenamePatterns = filenamePatternsArr;
                    } else {
                        delete policy.settings["filenamePatterns"];
                    }

                    policySettings.authorId = statusSettings.authorId.length ? statusSettings.authorId : null;
                    policySettings.defaultDisplayName = statusSettings.defaultDisplayName.length ? statusSettings.defaultDisplayName : null;
                    policySettings.invalidateOnSourceUpdate = statusSettings.invalidateOnSourceUpdate;
                    policySettings.policyApplicability = statusSettings.policyApplicability ? 1 : null;

                    const status = statusSettings.status;
                    let [statusName, statusGenre] = policyUtils.splitStatus(status);

                    // if status name or status genre have been entered separately
                    // then just consider these values
                    if (statusSettings.statusName || statusSettings.statusGenre) {
                        statusName = statusSettings.statusName ?? "";
                        statusGenre = statusSettings.statusGenre ?? "";
                    }

                    policySettings.statusName = statusName;
                    policySettings.statusGenre = statusGenre.length ? statusGenre : null;
                }

                const genre = policySettings.statusGenre && policySettings.statusGenre.length ? policySettings.statusGenre + "/" : "";

                const settings: IStatusPolicyComplexSettings = {
                    authorId: policySettings.authorId || "",
                    defaultDisplayName: policySettings.defaultDisplayName || "",
                    filenamePatterns:
                        policySettings.filenamePatterns && policySettings.filenamePatterns.length ? policySettings.filenamePatterns.join("; ") : "",
                    invalidateOnSourceUpdate: policySettings.invalidateOnSourceUpdate,
                    policyApplicability: !!policySettings.policyApplicability,
                    required: policy.isBlocking,
                    status: genre + policySettings.statusName,
                    statusGenre: policySettings.statusGenre || undefined,
                    statusName: policySettings.statusName
                };

                return {
                    complexSettingValueObject: settings
                };
            }

            return {
                complexSettingValueObject: { ...policySettingDefaults.status }
            };
        }
        if (type === policyTypes.requiredReviewers) {
            if (policy) {
                const policySettings = policy.settings as RequiredReviewerPolicySettings;
                if (complexSettingObject !== undefined) {
                    const reviewerSettings = complexSettingObject as IAutomaticReviewerPolicyComplexSettings;

                    policy.isBlocking = reviewerSettings.required;

                    let filenamePatternsArr: string[] | undefined;
                    if (reviewerSettings.filenamePatterns && reviewerSettings.filenamePatterns.length) {
                        filenamePatternsArr = reviewerSettings.filenamePatterns
                            .split(";")
                            .map(str => str.trim())
                            .filter(str => str.length > 0);
                    }

                    if (filenamePatternsArr && filenamePatternsArr.length) {
                        policySettings.filenamePatterns = filenamePatternsArr;
                    } else {
                        delete policy.settings["filenamePatterns"];
                    }

                    policySettings.creatorVoteCounts = reviewerSettings.creatorVoteCounts;
                    policySettings.message = reviewerSettings.message;
                    policySettings.minimumApproverCount = !reviewerSettings.required ? 1 : reviewerSettings.minimumApproverCount;
                    policySettings.requiredReviewerIds = reviewerSettings.reviewerIds;
                }

                const settings: IAutomaticReviewerPolicyComplexSettings = {
                    creatorVoteCounts: policySettings.creatorVoteCounts,
                    message: policySettings.message || "",
                    filenamePatterns:
                        policySettings.filenamePatterns && policySettings.filenamePatterns.length ? policySettings.filenamePatterns.join("; ") : "",
                    minimumApproverCount: !policy.isBlocking ? 1 : policySettings.minimumApproverCount || 1,
                    reviewerIds: policySettings.requiredReviewerIds,
                    reviewers: [],
                    required: policy.isBlocking
                };

                return {
                    complexSettingValueObject: settings
                };
            }

            return {
                complexSettingValueObject: { ...policySettingDefaults.reviewer }
            };
        }

        return undefined;
    },
    getStringFromArr: (arr: string[] | null | undefined, defaultVal: string): string => {
        return arr && arr.length ? arr.join("; ") : defaultVal;
    },
    getValue: (type: string, policy?: PolicyConfiguration, value?: boolean): boolean => {
        if (type === policyTypes.caseSensitivty) {
            // the case enforcement policy is special in that it is tied to repository settings instead of an individual policy
            // this means the "policy" is always enabled and instead the "setting" is turned on or off
            // actually disabling the policy will cause all sorts of weirdness - don't do it

            if (policy) {
                if (value !== undefined) {
                    policy.settings[policySettingKeys.caseSensitivity] = value || null;
                }

                return !!policy.settings[policySettingKeys.caseSensitivity];
            }
        }

        if (value !== undefined && policy) {
            policy.isEnabled = value;
        }

        return policy ? policy.isEnabled : false;
    },
    hasSettingValue: (policy: IPolicy): boolean => {
        if (policy.settingValue && policy.settingValue.length && policy.id !== -1) {
            return true;
        }

        return false;
    },
    setActiveAndInheritedDetails: (type: string, policy: IPolicy) => {
        const activeInherited = [];
        const activeManaged = [];

        if (policy.inheritedPolicies) {
            activeInherited.push(...policy.inheritedPolicies.filter(pol => pol.value));
        }

        if (policy.enterprisePolicies) {
            activeManaged.push(...policy.enterprisePolicies.filter(pol => pol.value));
        }

        if (activeManaged.length || activeInherited.length) {
            policy.inheritedValue = true;
            policy.activeInheritedAndEnterprisePolicies = [...activeInherited, ...activeManaged];
            policyUtils.setInheritedSettingValue(type, policy);
        }
    },
    setInheritedSettingValue: (type: string, policy: IPolicy) => {
        const policies = policy.activeInheritedAndEnterprisePolicies;
        if (policies && policies.length) {
            if (type === policyTypes.pathLength || type === policyTypes.fileSize) {
                // find min value
                const values = policies
                    .filter(pol => !!pol.settingValue && pol.settingValue.length > 0 && !isNaN(Number(pol.settingValue)))
                    .map(pol => Number(pol.settingValue));

                if (values && values.length) {
                    const minValue = Math.min(...values);
                    policy.inheritedSettingValue = minValue.toString();

                    if (policyUtils.hasSettingValue(policy) && !isNaN(Number(policy.settingValue)) && Number(policy.settingValue) <= minValue) {
                        policy.inheritedSettingValue = undefined;
                    }
                }
            } else if (type === policyTypes.linkedWorkItems || type === policyTypes.commentResolution) {
                const value = policies.find(pol => pol.settingValue === requiredOrOptional.required);
                if (value && policy.input) {
                    policy.input.disabled = true; // disable input in this scenario because user should not be able to change from required to optional
                }

                if (value) {
                    policy.inheritedSettingValue = requiredOrOptional.required;
                } else if (!value && policies.length && !policyUtils.hasSettingValue(policy)) {
                    policy.inheritedSettingValue = requiredOrOptional.optional;
                }
            } else if (type === policyTypes.limitMergeTypes) {
                const values = { ...policySettingDefaults.limitMergeTypes } as {
                    [key: string]: boolean;
                };

                const settingObj: {
                    [key: string]: boolean;
                }[] = policies.filter(pol => pol.settingValueObject != undefined).map(pol => pol.settingValueObject) as any;

                Object.keys(values).forEach(key => {
                    const disallowed = settingObj.find(setting => !setting[key]);
                    if (disallowed) {
                        delete values[key];
                    }
                });

                policy.inheritedSettingValueObject = values;
            } else if (type === policyTypes.minimumReviewers) {
                if (policy.id === -1) {
                    // if there isnt already a policy at this scope, then we want to override defaults with any inherited
                    const values = { ...policySettingDefaults.minimumReviewersObj } as {
                        [key: string]: boolean;
                    };

                    const sortedByWeight = policies
                        .filter(pol => pol.settingValue != undefined)
                        .sort((a, b) => calculateMinReviewerPolicyWeight(a) - calculateMinReviewerPolicyWeight(b));

                    if (sortedByWeight.length) {
                        policy.inheritedSettingValueObject = { ...sortedByWeight[0].settingValueObject };
                        policy.inheritedSettingValue = sortedByWeight[0].settingValue;
                    }
                }
            }
        }
    },
    splitStatus: (status: string): string[] => {
        let statusName = "";
        let statusGenre = "";
        let separatorIndex = -1;

        for (let i = status.length - 2; i >= 1; i--) {
            // find last valid / for word break
            if (status[i] === "/") {
                separatorIndex = i;
                break;
            }
        }

        if (separatorIndex <= 0) {
            statusName = status;
        } else {
            statusName = status.substring(separatorIndex + 1);
            statusGenre = status.substring(0, separatorIndex);
        }
        return [statusName, statusGenre];
    }
};

function calculateMinReviewerPolicyWeight(policy: IPolicy): number {
    let result = 0;

    if (!policy.settingValue) {
        return 100000;
    }

    result -= Number(policy.settingValue);

    if (policy.settingValueObject && policy.settingValueObject[policySettingKeys.minimumReviewersObj.creatorVoteCounts]) {
        // + half point if requestor can approve own changes
        result += 0.5;
    }

    if (policy.settingValueObject && policy.settingValueObject[policySettingKeys.minimumReviewersObj.requireVoteOnLastIteration]) {
        // take half a point if additional vote is required on last iteration
        result += 0.5;
    }

    if (policy.settingValueObject && policy.settingValueObject[policySettingKeys.minimumReviewersObj.resetOnSourcePush]) {
        if (policy.settingValueObject[policySettingKeys.minimumReviewersObj.resetRejectionsOnSourcePush]) {
            // - quarter point if all votes reset with new changes
            result -= 0.25;
        } else {
            // - half point if only approvals reset with new changes
            result -= 0.5;
        }
    }

    if (policy.settingValueObject && policy.settingValueObject[policySettingKeys.minimumReviewersObj.allowDownvotes]) {
        // + half point if can complete even with downvotes
        result += 0.5;
    }

    if (policy.settingValueObject && policy.settingValueObject[policySettingKeys.minimumReviewersObj.blockLastPusherVote]) {
        // - half point if block last pusher from approving
        result -= 0.5;
    }

    return result;
}

function convertDefinitionToListBoxItem(definition: BuildDefinitionSummary): IListBoxItem<BuildDefinitionSummary> {
    let fullBuildName = definition.name;

    if (definition.path && definition.path != "\\") {
        fullBuildName = `${definition.name} (${definition.path.substring(1)})`;
    }

    return {
        id: definition.id.toString(),
        text: fullBuildName,
        data: definition,
        disabled: !definition.hasQueueBuildPermission,
        tooltipProps: !definition.hasQueueBuildPermission ? { text: Resources.MissingQueueBuildPermissionOnPipelineTooltip } : undefined
    };
}

function convertToIdentity(identityRef: IdentityRef): IIdentity {
    return {
        entityId: "",
        entityType: identityRef.isContainer ? "Group" : "",
        originId: identityRef.id,
        localId: identityRef.id,
        originDirectory: "",
        displayName: identityRef.displayName,
        image: identityRef.imageUrl
    };
}

function processPolicyGroup(policyGroup: IPolicyGroup, type: string, isEditable: boolean): IPolicy[] {
    const result: IPolicy[] = [];

    if (policyGroup.enterpriseManagedPolicies) {
        for (const policy of policyGroup.enterpriseManagedPolicies) {
            result.push({
                ...policyUtils.getAsPolicy(type, policy),
                isEnterpriseManaged: true,
                disabled: !isEditable
            });
        }
    }

    if (policyGroup.inheritedPolicies) {
        for (const policy of policyGroup.inheritedPolicies) {
            result.push({
                ...policyUtils.getAsPolicy(type, policy),
                isInherited: true,
                disabled: !isEditable
            });
        }
    }

    if (policyGroup.currentScopePolicies) {
        for (const policy of policyGroup.currentScopePolicies) {
            result.push({ ...policyUtils.getAsPolicy(type, policy), disabled: !isEditable });
        }
    }

    return result;
}
